import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
  Alert,
  IconButton,
  Autocomplete,
  CircularProgress,
  Tooltip,
  Divider,
  Stack,
  InputAdornment,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  HelpOutline as HelpIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { useUser } from '../contexts/UserContext';
import {
  SiteAdmin,
  SiteAdminPermissions,
  SearchUser
} from '../types/SiteAdmin';

interface SiteAdminManagementProps {
  siteId?: string;
  siteName?: string;
  userPermissions?: SiteAdminPermissions;
}

// Single source of truth for permission labels + plain-language descriptions,
// rendered consistently across the table, chips and switches.
const PERMISSION_META: Array<{
  key: keyof SiteAdminPermissions;
  label: string;
  description: string;
}> = [
  { key: 'canManageEvents', label: '管理活動', description: '建立、編輯及刪除活動' },
  { key: 'canManageSettings', label: '管理設定', description: '修改群組設定與 WhatsApp 模板' },
  { key: 'canManageAdmins', label: '管理管理員', description: '邀請、移除管理員及調整權限' },
  { key: 'canManageBlacklist', label: '管理黑名單', description: '新增或移除黑名單成員' },
  { key: 'canViewUsage', label: '查看使用狀況', description: '檢視 WhatsApp 發送統計' },
  { key: 'canSendPaymentReminder', label: '發送付款提醒', description: '向參加者發送付款提醒訊息' }
];

const ALL_PERMISSIONS_FALSE: SiteAdminPermissions = {
  canManageEvents: false,
  canManageSettings: false,
  canManageAdmins: false,
  canManageBlacklist: false,
  canViewUsage: false,
  canSendPaymentReminder: false
};

// Role presets so owners can grant a sensible set of permissions in one tap
// instead of toggling six switches every time.
const PERMISSION_PRESETS: Array<{
  name: string;
  description: string;
  permissions: SiteAdminPermissions;
}> = [
  {
    name: '活動小幫手',
    description: '管理活動、黑名單、付款提醒及查看使用狀況',
    permissions: {
      canManageEvents: true,
      canManageSettings: false,
      canManageAdmins: false,
      canManageBlacklist: true,
      canViewUsage: true,
      canSendPaymentReminder: true
    }
  },
  {
    name: '完整管理員',
    description: '擁有除轉移擁有權外的所有權限',
    permissions: {
      canManageEvents: true,
      canManageSettings: true,
      canManageAdmins: true,
      canManageBlacklist: true,
      canViewUsage: true,
      canSendPaymentReminder: true
    }
  },
  {
    name: '唯讀檢視',
    description: '僅能查看使用狀況，無法修改任何內容',
    permissions: {
      ...ALL_PERMISSIONS_FALSE,
      canViewUsage: true
    }
  }
];

const DEFAULT_INVITE_PERMISSIONS = PERMISSION_PRESETS[0].permissions;

const STATUS_META: Record<string, { label: string; color: 'success' | 'warning' | 'default' }> = {
  active: { label: '已啟用', color: 'success' },
  invited: { label: '待接受', color: 'warning' },
  suspended: { label: '已停用', color: 'default' }
};

const arePermissionsEqual = (a: SiteAdminPermissions, b: SiteAdminPermissions) =>
  PERMISSION_META.every(({ key }) => a[key] === b[key]);

const countGranted = (permissions: SiteAdminPermissions) =>
  PERMISSION_META.filter(({ key }) => permissions[key]).length;

const SiteAdminManagement: React.FC<SiteAdminManagementProps> = ({
  siteId,
  siteName,
  userPermissions
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useUser();
  const currentUserId = user?.userId;

  const [admins, setAdmins] = useState<SiteAdmin[]>([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Add admin dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [searchUsers, setSearchUsers] = useState<SearchUser[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SearchUser | null>(null);
  const [invitePermissions, setInvitePermissions] = useState<SiteAdminPermissions>(DEFAULT_INVITE_PERMISSIONS);

  // Edit permissions dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<SiteAdmin | null>(null);
  const [editPermissions, setEditPermissions] = useState<SiteAdminPermissions>(DEFAULT_INVITE_PERMISSIONS);

  // Remove confirmation dialog state
  const [removingAdmin, setRemovingAdmin] = useState<SiteAdmin | null>(null);
  const [removing, setRemoving] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  // Auto-dismiss success messages so they don't linger.
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  const fetchAdmins = useCallback(async () => {
    if (!siteId) return;

    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/site-admin/${siteId}/admins`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAdmins(response.data.admins);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || '載入管理員失敗');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    if (userPermissions?.canManageAdmins && siteId) {
      fetchAdmins();
    }
  }, [siteId, userPermissions?.canManageAdmins, fetchAdmins]);

  const searchUsersForInvite = async (query: string) => {
    if (query.length < 2 || !siteId) {
      setSearchUsers([]);
      return;
    }

    try {
      setSearchLoading(true);
      const response = await axios.get(`${API_BASE_URL}/site-admin/${siteId}/search-users`, {
        params: { query },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setSearchUsers(response.data.users);
    } catch (err: any) {
      console.error('Search users error:', err);
      setSearchUsers([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleInviteAdmin = async () => {
    if (!selectedUser || !siteId) return;

    try {
      setSubmitting(true);
      await axios.post(`${API_BASE_URL}/site-admin/${siteId}/admins`, {
        username: selectedUser.username,
        permissions: invitePermissions
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setSuccess(`已邀請 ${selectedUser.username} 成為管理員`);
      setAddDialogOpen(false);
      resetAddDialog();
      fetchAdmins();
    } catch (err: any) {
      setError(err.response?.data?.message || '邀請管理員失敗');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveAdmin = async () => {
    if (!removingAdmin || !siteId) return;

    try {
      setRemoving(true);
      await axios.delete(`${API_BASE_URL}/site-admin/${siteId}/admins/${removingAdmin._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setSuccess(`已移除 ${removingAdmin.username} 的管理員權限`);
      setRemovingAdmin(null);
      fetchAdmins();
    } catch (err: any) {
      setError(err.response?.data?.message || '移除管理員失敗');
    } finally {
      setRemoving(false);
    }
  };

  const handleUpdatePermissions = async () => {
    if (!editingAdmin || !siteId) return;

    try {
      setSubmitting(true);
      await axios.put(`${API_BASE_URL}/site-admin/${siteId}/admins/${editingAdmin._id}/permissions`, {
        permissions: editPermissions
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      setSuccess(`已更新 ${editingAdmin.username} 的權限`);
      setEditDialogOpen(false);
      setEditingAdmin(null);
      fetchAdmins();
    } catch (err: any) {
      setError(err.response?.data?.message || '更新權限失敗');
    } finally {
      setSubmitting(false);
    }
  };

  const resetAddDialog = () => {
    setSelectedUser(null);
    setSearchUsers([]);
    setInvitePermissions(DEFAULT_INVITE_PERMISSIONS);
  };

  const openEditDialog = (admin: SiteAdmin) => {
    setEditingAdmin(admin);
    setEditPermissions(admin.permissions);
    setEditDialogOpen(true);
  };

  const renderStatusChip = (status?: SiteAdmin['status']) => {
    if (!status) return null;
    const meta = STATUS_META[status] || STATUS_META.active;
    return <Chip label={meta.label} color={meta.color} size="small" variant="outlined" />;
  };

  const renderPermissionChips = (permissions: SiteAdminPermissions) => {
    const granted = PERMISSION_META.filter(({ key }) => permissions[key]);

    if (granted.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          無任何權限
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {granted.map(({ key, label }) => (
          <Chip key={key} label={label} size="small" color="primary" variant="outlined" />
        ))}
      </Box>
    );
  };

  // Permission editor shared by the invite and edit dialogs: presets for quick
  // setup, then per-permission rows with descriptions so the impact is clear.
  const renderPermissionEditor = (
    permissions: SiteAdminPermissions,
    setPermissions: React.Dispatch<React.SetStateAction<SiteAdminPermissions>>
  ) => {
    const grantedCount = PERMISSION_META.filter(({ key }) => permissions[key]).length;
    const allGranted = grantedCount === PERMISSION_META.length;

    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            快速套用
          </Typography>
          <Button
            size="small"
            onClick={() =>
              setPermissions(allGranted ? { ...ALL_PERMISSIONS_FALSE } : {
                canManageEvents: true,
                canManageSettings: true,
                canManageAdmins: true,
                canManageBlacklist: true,
                canViewUsage: true,
                canSendPaymentReminder: true
              })
            }
          >
            {allGranted ? '全部取消' : '全部選取'}
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {PERMISSION_PRESETS.map((preset) => (
            <Tooltip key={preset.name} title={preset.description}>
              <Chip
                label={preset.name}
                onClick={() => setPermissions({ ...preset.permissions })}
                color={arePermissionsEqual(permissions, preset.permissions) ? 'primary' : 'default'}
                variant={arePermissionsEqual(permissions, preset.permissions) ? 'filled' : 'outlined'}
                clickable
              />
            </Tooltip>
          ))}
        </Box>

        {grantedCount === 0 && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            未選擇任何權限，此管理員將無法執行任何操作。
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography variant="subtitle2" color="text.secondary">
            個別權限
          </Typography>
          <Typography variant="caption" color="text.secondary">
            已選 {grantedCount} / {PERMISSION_META.length} 項
          </Typography>
        </Box>
        <Divider sx={{ mb: 1 }} />

        <Stack divider={<Divider flexItem />}>
          {PERMISSION_META.map(({ key, label, description }) => (
            <FormControlLabel
              key={key}
              sx={{ m: 0, py: 1, justifyContent: 'space-between', width: '100%' }}
              labelPlacement="start"
              control={
                <Switch
                  checked={permissions[key]}
                  onChange={(e) =>
                    setPermissions((prev) => ({ ...prev, [key]: e.target.checked }))
                  }
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {description}
                  </Typography>
                </Box>
              }
            />
          ))}
        </Stack>
      </Box>
    );
  };

  if (!userPermissions?.canManageAdmins) {
    return (
      <Alert severity="info">
        您沒有權限管理群組管理員。
      </Alert>
    );
  }

  const renderActions = (admin: SiteAdmin) =>
    admin.role !== 'owner' ? (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Tooltip title="編輯權限">
          <IconButton size="small" onClick={() => openEditDialog(admin)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="移除管理員">
          <IconButton size="small" color="error" onClick={() => setRemovingAdmin(admin)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ) : (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );

  const renderYouBadge = (admin: SiteAdmin) =>
    admin._id === currentUserId ? (
      <Chip label="你" size="small" color="info" sx={{ height: 18, fontSize: '0.65rem' }} />
    ) : null;

  // Mobile uses stacked cards; the multi-column table overflows on small screens.
  const renderMobileList = () => (
    <Stack spacing={2}>
      {filteredAdmins.map((admin) => (
        <Card key={admin._id} variant="outlined">
          <CardContent sx={{ '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                {admin.role === 'owner' ? <AdminIcon color="primary" /> : <PersonIcon color="action" />}
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" fontWeight="bold" noWrap>
                      {admin.username}
                    </Typography>
                    {renderYouBadge(admin)}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {admin.phoneNumber}
                  </Typography>
                </Box>
              </Box>
              {renderActions(admin)}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
              <Chip
                label={admin.role === 'owner' ? '擁有者' : '管理員'}
                color={admin.role === 'owner' ? 'primary' : 'default'}
                size="small"
              />
              {renderStatusChip(admin.status)}
            </Box>

            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                權限
              </Typography>
              {renderPermissionChips(admin.permissions)}
            </Box>
          </CardContent>
        </Card>
      ))}
    </Stack>
  );

  const renderTable = () => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>管理員</TableCell>
            <TableCell>角色</TableCell>
            <TableCell>狀態</TableCell>
            <TableCell>權限</TableCell>
            <TableCell>加入時間</TableCell>
            <TableCell align="right">操作</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredAdmins.map((admin) => (
            <TableRow key={admin._id}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {admin.role === 'owner' ? <AdminIcon color="primary" /> : <PersonIcon color="action" />}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight="bold">
                        {admin.username}
                      </Typography>
                      {renderYouBadge(admin)}
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {admin.phoneNumber}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={admin.role === 'owner' ? '擁有者' : '管理員'}
                  color={admin.role === 'owner' ? 'primary' : 'default'}
                  size="small"
                />
              </TableCell>
              <TableCell>{renderStatusChip(admin.status)}</TableCell>
              <TableCell>{renderPermissionChips(admin.permissions)}</TableCell>
              <TableCell>
                <Typography variant="caption">
                  {new Date(admin.invitedAt).toLocaleDateString()}
                </Typography>
              </TableCell>
              <TableCell align="right">{renderActions(admin)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const hasInvitedAdmins = admins.some((admin) => admin.role !== 'owner');
  const normalizedFilter = filter.trim().toLowerCase();
  const filteredAdmins = normalizedFilter
    ? admins.filter(
        (admin) =>
          admin.username.toLowerCase().includes(normalizedFilter) ||
          (admin.phoneNumber || '').includes(normalizedFilter)
      )
    : admins;
  const showFilter = admins.length > 5;

  return (
    <Box>
      {/* Intro explains what this feature is for at a glance. */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AdminIcon color="primary" /> 管理員
            {!loading && admins.length > 0 && (
              <Chip label={`共 ${admins.length} 位`} size="small" />
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 520 }}>
            邀請其他成員協助管理 {siteName || '此群組'}，並依需要授予不同權限。擁有者擁有全部權限且無法被移除。
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          邀請管理員
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {!hasInvitedAdmins && (
                <Alert severity="info" icon={<HelpIcon />} sx={{ mb: 2 }}>
                  目前只有擁有者。點擊「邀請管理員」加入協助管理的成員。
                </Alert>
              )}
              {showFilter && (
                <TextField
                  fullWidth
                  size="small"
                  placeholder="搜尋管理員名稱或電話"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    )
                  }}
                />
              )}
              {filteredAdmins.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  找不到符合「{filter}」的管理員
                </Typography>
              ) : isMobile ? (
                renderMobileList()
              ) : (
                renderTable()
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Add Admin Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>邀請群組管理員</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Autocomplete
              options={searchUsers}
              getOptionLabel={(option) => `${option.username} (${option.phoneNumber})`}
              loading={searchLoading}
              noOptionsText="找不到使用者"
              onInputChange={(event, newInputValue) => {
                searchUsersForInvite(newInputValue);
              }}
              onChange={(event, newValue) => {
                setSelectedUser(newValue);
              }}
              value={selectedUser}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="搜尋使用者名稱"
                  placeholder="輸入至少2個字元進行搜尋..."
                  fullWidth
                  helperText="依使用者名稱搜尋並選擇要邀請的成員"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchLoading && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              sx={{ mb: 3 }}
            />

            {selectedUser && (
              <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  {selectedUser.username} 的權限設定
                </Typography>
                {renderPermissionEditor(invitePermissions, setInvitePermissions)}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddDialogOpen(false); resetAddDialog(); }}>
            取消
          </Button>
          <Button
            onClick={handleInviteAdmin}
            variant="contained"
            disabled={!selectedUser || submitting || countGranted(invitePermissions) === 0}
          >
            {submitting ? <CircularProgress size={20} /> : '送出邀請'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Permissions Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          編輯 {editingAdmin?.username} 的權限
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {renderPermissionEditor(editPermissions, setEditPermissions)}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            取消
          </Button>
          <Button
            onClick={handleUpdatePermissions}
            variant="contained"
            disabled={
              submitting ||
              countGranted(editPermissions) === 0 ||
              (!!editingAdmin && arePermissionsEqual(editPermissions, editingAdmin.permissions))
            }
          >
            {submitting ? <CircularProgress size={20} /> : '儲存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Admin Confirmation Dialog */}
      <Dialog open={!!removingAdmin} onClose={() => setRemovingAdmin(null)} maxWidth="xs" fullWidth>
        <DialogTitle>移除管理員</DialogTitle>
        <DialogContent>
          <DialogContentText>
            確定要移除 <strong>{removingAdmin?.username}</strong> 的管理員權限嗎？此成員將無法再協助管理此群組。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemovingAdmin(null)}>取消</Button>
          <Button onClick={handleRemoveAdmin} color="error" variant="contained" disabled={removing}>
            {removing ? <CircularProgress size={20} /> : '移除'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SiteAdminManagement;
